import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { ApiError, apiFetch } from '../lib/api';
import { ORACORD_APP_DOWNLOAD_URL } from '../lib/config';
import { acceptedInvitationMessage, type AcceptedStaffInvitationStatus } from '../lib/invitations';
import {
  consumePendingInvitationAuthorization,
  markPendingInvitationAuthorization,
  shouldContinueAfterInviteAcceptError,
} from '../lib/accept-invitation-flow';
import { AuthCard, primaryButtonClass, secondaryButtonClass, ErrorBanner, NoticeBanner } from '../components/AuthCard';

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const invitationIdFromUrl = params.get('id') ?? params.get('invitationId') ?? params.get('invitation_id') ?? '';
  const [savedInvitationId, setSavedInvitationId] = useState(() => (
    typeof window === 'undefined' ? '' : window.sessionStorage.getItem('oracord.invitationId') ?? ''
  ));
  const invitationId = invitationIdFromUrl || savedInvitationId;
  const { data, isPending } = authClient.useSession();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [authorizationSubmitting, setAuthorizationSubmitting] = useState(false);
  const [acceptedStatus, setAcceptedStatus] = useState<AcceptedStaffInvitationStatus | null>(null);

  function acceptPath() {
    return invitationId ? `/accept-invitation?id=${encodeURIComponent(invitationId)}` : '/accept-invitation';
  }

  async function continueWithGoogleWorkspace() {
    setAuthorizationSubmitting(true);
    setError(null);
    const callbackURL = typeof window !== 'undefined' ? `${window.location.origin}${acceptPath()}` : acceptPath();
    if (typeof window !== 'undefined' && invitationId) {
      markPendingInvitationAuthorization(window.sessionStorage, invitationId);
    }
    const { error: err } = await authClient.signIn.social({ provider: 'google', callbackURL });
    if (err) {
      setError(err.message || 'Google Workspace sign-in failed.');
      setAuthorizationSubmitting(false);
    }
  }

  async function startGoogleAuthorization() {
    setAuthorizationSubmitting(true);
    setError(null);
    try {
      const authorization = await apiFetch<{ authorizationUrl?: string; redirectAvailable?: boolean }>('/api/auth/google-workspace/authorization/start-from-invitation', {
        method: 'POST',
        body: invitationId ? { invitationId } : {},
        skipOrgHeader: true,
      });
      if (authorization.authorizationUrl) {
        window.sessionStorage.removeItem('oracord.invitationId');
        window.location.assign(authorization.authorizationUrl);
        return;
      }
      setError('Gmail and Calendar authorization is not ready yet. Ask your admin to check Google Workspace setup.');
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { status?: string; error?: string } | null;
        if (body?.status === 'google-account-required') {
          await continueWithGoogleWorkspace();
          return;
        }
      }
      setError((err as Error).message || 'Could not start Gmail and Calendar authorization.');
    } finally {
      setAuthorizationSubmitting(false);
    }
  }

  async function finalizeInvitationAcceptance() {
    if (invitationId) {
      const result = await authClient.organization.acceptInvitation({ invitationId });
      if (result?.error && !shouldContinueAfterInviteAcceptError(result.error)) {
        setError(result.error.message || 'Could not accept invitation.');
        return false;
      }
    }
    const linkResult = await apiFetch<{ status: AcceptedStaffInvitationStatus }>('/api/staff-invitations/accepted', {
      method: 'POST',
      body: invitationId ? { invitationId } : {},
      skipOrgHeader: true,
    });
    setAcceptedStatus(linkResult.status);
    return true;
  }

  async function accept() {
    setSubmitting(true);
    setError(null);
    try {
      await finalizeInvitationAcceptance();
    } catch (err) {
      setError((err as Error).message || 'Could not accept invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  async function authorizeGmailAndCalendar() {
    setSubmitting(true);
    setError(null);
    try {
      const finalized = acceptedStatus ? true : await finalizeInvitationAcceptance();
      if (!finalized) return;
      await startGoogleAuthorization();
    } catch (err) {
      setError((err as Error).message || 'Could not start Gmail and Calendar authorization.');
    } finally {
      setSubmitting(false);
    }
  }

  async function switchAccount() {
    await authClient.signOut();
    window.location.href = `/login?next=${encodeURIComponent(acceptPath())}`;
  }

  useEffect(() => {
    if (!invitationIdFromUrl) return;
    window.sessionStorage.setItem('oracord.invitationId', invitationIdFromUrl);
    setSavedInvitationId(invitationIdFromUrl);
  }, [invitationIdFromUrl]);

  useEffect(() => {
    if (isPending || !data?.user || !invitationId || typeof window === 'undefined') return;
    if (!consumePendingInvitationAuthorization(window.sessionStorage, invitationId)) return;
    void startGoogleAuthorization();
  }, [data?.user, invitationId, isPending]);

  if (isPending) {
    return <AuthCard title="Accept invitation"><div className="text-sm text-slate-500">Loading…</div></AuthCard>;
  }

  if (!invitationId) {
    if (data?.user) {
      return (
        <AuthCard title="Gmail and Calendar setup" subtitle="Recovering your accepted invitation from your signed-in work account.">
          <div className="space-y-3 text-sm">
            <ErrorBanner message={error} />
            <NoticeBanner tone="amber">
              The browser dropped the invitation id from the link. Oracord is checking your signed-in work account for an already accepted invite so it can continue to Google authorization.
            </NoticeBanner>
            <button type="button" onClick={authorizeGmailAndCalendar} disabled={submitting || authorizationSubmitting} className={primaryButtonClass}>
              {submitting || authorizationSubmitting ? 'Starting Google authorization…' : 'Authorize Gmail and Calendar with Google'}
            </button>
            <button type="button" onClick={switchAccount} disabled={authorizationSubmitting} className={secondaryButtonClass}>
              Switch account
            </button>
          </div>
        </AuthCard>
      );
    }
    return (
      <AuthCard title="Accept invitation" subtitle="This invitation link is incomplete.">
        <NoticeBanner tone="amber">
          Missing invitation id. Open the original invite email link again so Oracord can finish setup with the right work account.
        </NoticeBanner>
      </AuthCard>
    );
  }

  if (!data?.user) {
    const next = encodeURIComponent(`/accept-invitation?id=${invitationId}`);
    return (
      <AuthCard title="Authorize Gmail and Calendar" subtitle="Sign in with the work Google account your admin added to Oracord.">
        <div className="space-y-3 text-sm">
          <Link to={`/login?next=${next}`} className={`${primaryButtonClass} no-underline`}>Sign in to continue</Link>
          <Link to={`/signup?next=${next}`} className={secondaryButtonClass}>Create account</Link>
        </div>
      </AuthCard>
    );
  }

  if (acceptedStatus) {
    return (
      <AuthCard title="Gmail and Calendar setup" subtitle={acceptedInvitationMessage(acceptedStatus)}>
        <div className="space-y-3 text-sm">
          <ErrorBanner message={error} />
          <NoticeBanner tone="emerald">
            You’ve been added by your admin. The remaining step is approving Gmail and Calendar access with your work Google account.
          </NoticeBanner>
          <button type="button" onClick={authorizeGmailAndCalendar} disabled={submitting || authorizationSubmitting} className={primaryButtonClass}>
            {submitting || authorizationSubmitting ? 'Starting Google authorization…' : 'Authorize Gmail and Calendar with Google'}
          </button>
          {ORACORD_APP_DOWNLOAD_URL ? (
            <a href={ORACORD_APP_DOWNLOAD_URL} className={`${secondaryButtonClass} w-full no-underline`}>
              Download the Oracord app
            </a>
          ) : (
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs leading-5 text-slate-500">
              Your manager will share the Oracord app download link when it’s ready.
            </p>
          )}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Authorize Gmail and Calendar" subtitle="Finishing the setup link from your admin…">
      {params.get('verified') === '1' && (
        <NoticeBanner tone="emerald" className="mb-4">
          Email verified. Finishing your invitation now.
        </NoticeBanner>
      )}
      <ErrorBanner message={error} />
      <div className="space-y-4 text-sm text-slate-600">
        <p>You’re signed in as <strong>{data.user.email}</strong>.</p>
        {submitting && (
          <NoticeBanner tone="emerald">
            Confirming your team access before Google authorization…
          </NoticeBanner>
        )}
        {!submitting && !error && (
          <button type="button" onClick={authorizeGmailAndCalendar} disabled={submitting || authorizationSubmitting} className={primaryButtonClass}>
            Authorize Gmail and Calendar with Google
          </button>
        )}
        {!submitting && error && (
          <p className="text-xs leading-5 text-slate-500">
            If this is not the email that received the invite, switch accounts first.
          </p>
        )}
        {!submitting && error && (
          <button type="button" onClick={accept} disabled={submitting} className={primaryButtonClass}>
            Try accepting again
          </button>
        )}
        <button type="button" onClick={switchAccount} disabled={submitting} className={secondaryButtonClass}>
          Switch account
        </button>
      </div>
    </AuthCard>
  );
}
